export const dashboard = {
  async dashboard() {
    let stats = { data: { ai_portfolio: { total_assets: 0, risk_distribution: {}, status_distribution: {}, category_distribution: {} }, risk_assessments: {}, monitoring: {}, open_incidents: {}, compliance: {} } };
    let onboarding = { data: { steps: [], percentage: 100, completed: 0, total: 0 } };
    try {
      [stats, onboarding] = await Promise.all([
        API.getDashboardStats(),
        API.getOnboardingProgress(),
      ]);
    } catch (e) { /* use defaults */ }
    const d = stats.data;
    const total = d.ai_portfolio.total_assets;
    const risk = d.ai_portfolio.risk_distribution;
    const comp = d.compliance;
    const ob = onboarding.data;

    const showOnboarding = ob.percentage < 100;

    return `
      <div class="page-header">
        <div><h2>AI Governance Dashboard</h2><p>Portfolio overview and compliance status for ${API.tenant?.name || 'your organization'}</p></div>
      </div>
      ${showOnboarding ? `
      <div class="card onboarding-card" style="margin-bottom:24px">
        <div class="card-header">
          <h3>Getting Started - ${ob.completed}/${ob.total} Complete</h3>
          <button class="btn btn-sm btn-outline" data-action="Pages.dismissOnboarding">Dismiss</button>
        </div>
        <div class="onboarding-progress-bar" style="margin-bottom:16px">
          <div class="onboarding-track">
            <div class="onboarding-fill" style="width:${ob.percentage}%"></div>
          </div>
          <span class="onboarding-pct">${ob.percentage}%</span>
        </div>
        <div class="onboarding-steps">
          ${ob.steps.map(s => `
            <div class="onboarding-step ${s.completed ? 'completed' : ''}" ${!s.completed ? `data-onboarding-step="${s.key}" role="button" tabindex="0"` : ''}>
              <span class="step-check">${s.completed ? '&#10003;' : '&#9675;'}</span>
              <div class="step-info">
                <strong>${s.label}</strong>
                <span class="step-desc">${s.description}</span>
              </div>
            </div>
          `).join('')}
        </div>
      </div>` : ''}
      <div class="stats-row">
        <div class="stat-card info clickable" data-dash-nav="ai-assets" role="button" tabindex="0" title="View AI asset registry">
          <div class="stat-value">${total}</div>
          <div class="stat-label">AI Systems in Portfolio</div>
        </div>
        <div class="stat-card critical clickable" data-dash-nav="ai-assets" data-dash-filter='{"risk_tier":"high_or_critical"}' role="button" tabindex="0" title="View high/critical risk systems">
          <div class="stat-value">${(risk.critical || 0) + (risk.high || 0)}</div>
          <div class="stat-label">High/Critical Risk Systems</div>
        </div>
        <div class="stat-card warning clickable" data-dash-nav="monitoring" role="button" tabindex="0" title="View monitoring alerts">
          <div class="stat-value">${d.monitoring.alerts_last_30_days || 0}</div>
          <div class="stat-label">Alerts (30 Days)</div>
        </div>
        <div class="stat-card success clickable" data-dash-nav="compliance" role="button" tabindex="0" title="View compliance status">
          <div class="stat-value">${comp.compliance_percentage || 0}%</div>
          <div class="stat-label">Compliance Score</div>
        </div>
      </div>
      <div class="card-grid grid-2">
        <div class="card">
          <div class="card-header"><h3>Risk Distribution</h3><span class="text-muted" style="font-size:11px">Click a tier to filter</span></div>
          ${total > 0 ? `
          <div class="card-body">
            <div class="risk-bar">
              ${risk.critical ? `<div class="segment critical clickable" data-dash-nav="ai-assets" data-dash-filter='{"risk_tier":"critical"}' title="View critical-tier assets" style="width:${(risk.critical/total*100)}%">${risk.critical}</div>` : ''}
              ${risk.high ? `<div class="segment high clickable" data-dash-nav="ai-assets" data-dash-filter='{"risk_tier":"high"}' title="View high-tier assets" style="width:${(risk.high/total*100)}%">${risk.high}</div>` : ''}
              ${risk.moderate ? `<div class="segment moderate clickable" data-dash-nav="ai-assets" data-dash-filter='{"risk_tier":"moderate"}' title="View moderate-tier assets" style="width:${(risk.moderate/total*100)}%">${risk.moderate}</div>` : ''}
              ${risk.low ? `<div class="segment low clickable" data-dash-nav="ai-assets" data-dash-filter='{"risk_tier":"low"}' title="View low-tier assets" style="width:${(risk.low/total*100)}%">${risk.low}</div>` : ''}
            </div>
            <div class="risk-legend">
              <span class="legend-chip clickable" data-dash-nav="ai-assets" data-dash-filter='{"risk_tier":"critical"}' role="button" tabindex="0"><span class="dot" style="background:var(--danger)"></span> Critical (${risk.critical||0})</span>
              <span class="legend-chip clickable" data-dash-nav="ai-assets" data-dash-filter='{"risk_tier":"high"}' role="button" tabindex="0"><span class="dot" style="background:#ef4444"></span> High (${risk.high||0})</span>
              <span class="legend-chip clickable" data-dash-nav="ai-assets" data-dash-filter='{"risk_tier":"moderate"}' role="button" tabindex="0"><span class="dot" style="background:var(--warning)"></span> Moderate (${risk.moderate||0})</span>
              <span class="legend-chip clickable" data-dash-nav="ai-assets" data-dash-filter='{"risk_tier":"low"}' role="button" tabindex="0"><span class="dot" style="background:var(--success)"></span> Low (${risk.low||0})</span>
            </div>
          </div>` : `<div class="card-body"><div class="empty-state"><p>No AI assets registered yet</p><button class="btn btn-primary" data-dash-nav="ai-assets">Register your first AI asset</button></div></div>`}
        </div>
        <div class="card">
          <div class="card-header"><h3>Compliance Status</h3><span class="text-muted" style="font-size:11px">Click to view controls</span></div>
          <div class="card-body">
            <div class="clickable" data-dash-nav="compliance" role="button" tabindex="0" style="text-align:center;padding:var(--sp-2);border-radius:var(--radius);transition:background var(--motion-fast)">
              <div class="compliance-gauge">
                <svg width="120" height="120" viewBox="0 0 120 120">
                  <circle cx="60" cy="60" r="50" fill="none" stroke="#e2e8f0" stroke-width="10"/>
                  <circle cx="60" cy="60" r="50" fill="none" stroke="#14b8a6" stroke-width="10"
                    stroke-dasharray="${(comp.compliance_percentage||0)*3.14} 314" stroke-linecap="round"/>
                </svg>
                <div class="gauge-text">${comp.compliance_percentage||0}%</div>
              </div>
              <div style="font-size:12px;color:var(--text-secondary);margin-top:var(--sp-2)">
                <span class="clickable inline-link" data-dash-nav="compliance" data-dash-filter='{"status":"implemented"}'>Implemented: ${comp.implemented||0}</span> |
                <span class="clickable inline-link" data-dash-nav="compliance" data-dash-filter='{"status":"partially_implemented"}'>Partial: ${comp.partially_implemented||0}</span> |
                <span class="clickable inline-link" data-dash-nav="compliance" data-dash-filter='{"status":"planned"}'>Planned: ${comp.planned||0}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="card-grid grid-2" style="margin-top:16px">
        <div class="card">
          <div class="card-header"><h3>Assessment Status</h3><span class="text-muted" style="font-size:11px">Click a row to view</span></div>
          <table>
            <thead><tr><th>Status</th><th class="text-right">Count</th></tr></thead>
            <tbody>
              ${Object.keys(d.risk_assessments).length > 0 ? Object.entries(d.risk_assessments).map(([s,c]) =>
                `<tr class="clickable" data-dash-nav="risk-assessments" data-dash-filter='${JSON.stringify({status:s})}' role="button" tabindex="0"><td>${App.badge(s)}</td><td class="text-right">${c}</td></tr>`
              ).join('') : '<tr><td colspan="2" class="empty-state">No assessments yet. <a href="#" data-dash-nav="risk-assessments">Start your first risk assessment →</a></td></tr>'}
            </tbody>
          </table>
        </div>
        <div class="card">
          <div class="card-header"><h3>Open Incidents</h3>${Object.keys(d.open_incidents).length > 0 ? '<span class="text-muted" style="font-size:11px">Click a row to view</span>' : ''}</div>
          ${Object.keys(d.open_incidents).length > 0 ? `<table>
            <thead><tr><th>Severity</th><th class="text-right">Count</th></tr></thead>
            <tbody>
              ${Object.entries(d.open_incidents).map(([s,c]) =>
                `<tr class="clickable" data-dash-nav="incidents" data-dash-filter='${JSON.stringify({severity:s,status:"open"})}' role="button" tabindex="0"><td>${App.badge(s)}</td><td class="text-right">${c}</td></tr>`
              ).join('')}
            </tbody>
          </table>` : '<div class="empty-state"><p>No open incidents</p></div>'}
        </div>
      </div>`;
  },

  onboardingNavigate(step) {
    const routes = {
      add_asset: 'ai-assets', risk_assessment: 'risk-assessments',
      impact_assessment: 'impact-assessments', compliance: 'compliance',
      vendor_assessment: 'vendors', maturity: 'maturity', invite_team: 'users',
    };
    if (routes[step]) App.navigate(routes[step]);
  },

  dismissOnboarding() {
    const card = document.querySelector('.onboarding-card');
    if (card) card.style.display = 'none';
  },

  dashboardInit() {
    // Onboarding step cards
    document.querySelectorAll('[data-onboarding-step]').forEach(el => {
      const key = el.getAttribute('data-onboarding-step');
      el.addEventListener('click', () => window.Pages?.onboardingNavigate?.(key));
      el.addEventListener('keydown', (ev) => {
        if (ev.key === 'Enter' || ev.key === ' ') { ev.preventDefault(); window.Pages?.onboardingNavigate?.(key); }
      });
    });

    // Drill-down: every element with data-dash-nav navigates to the
    // corresponding page. If data-dash-filter is present, the filter is
    // stashed on window._navFilter for the target page to read and apply.
    const trigger = (el) => {
      const page = el.getAttribute('data-dash-nav');
      const rawFilter = el.getAttribute('data-dash-filter');
      if (rawFilter) {
        try { window._navFilter = { page, filter: JSON.parse(rawFilter) }; }
        catch { window._navFilter = null; }
      } else {
        window._navFilter = null;
      }
      if (page) App.navigate(page);
    };
    document.querySelectorAll('[data-dash-nav]').forEach(el => {
      el.addEventListener('click', (ev) => { ev.preventDefault(); trigger(el); });
      if (el.getAttribute('role') === 'button') {
        el.addEventListener('keydown', (ev) => {
          if (ev.key === 'Enter' || ev.key === ' ') { ev.preventDefault(); trigger(el); }
        });
      }
    });
  },
};
